import { useFlowControlStore } from '@components/flow-controller' // TODO: migrate off deprecated package
import { onBeforeUnmount, onMounted } from 'vue'

import { locationAssign } from '@/helpers/navigation'

import { errorTracker } from '@/services/errorTracking'

import { useCartStore } from '@/stores/cart'
import { useGlobalError } from '@/stores/useGlobalErrorStore'

import { openDiscardWarningModal } from '@/components/openDiscardWarningModal'

function isModifiedClick(event: MouseEvent): boolean {
  return (
    event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
  )
}

function shouldInterceptLink(anchor: HTMLAnchorElement, location: Location): boolean {
  if (anchor.target && anchor.target !== '_self') return false

  const href = anchor.getAttribute('href')
  if (href && (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:'))) return false

  const next = new URL(anchor.href)
  return next.origin !== location.origin || next.pathname !== location.pathname || next.search !== location.search
}

export function useIncompleteBundleLeaveModal(): void {
  const flowStore = useFlowControlStore()
  const cartStore = useCartStore()
  const globalError = useGlobalError()

  let modalIsOpen = false

  function incompleteIds(): string[] {
    if (!flowStore.cartSnapshot || flowStore.isComplete) return []
    return flowStore.cartSnapshot.flowResolutions
      .filter((resolution) => resolution.state !== 'completed')
      .map((resolution) => resolution.id)
  }

  function onBeforeUnload(event: BeforeUnloadEvent): void {
    if (modalIsOpen || incompleteIds().length === 0) return
    event.preventDefault()
    event.returnValue = true
  }

  function onDocumentClick(event: MouseEvent): void {
    if (modalIsOpen || isModifiedClick(event) || incompleteIds().length === 0) return

    const link = (event.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null
    if (link && shouldInterceptLink(link, window.location)) {
      event.preventDefault()
      modalIsOpen = true

      openDiscardWarningModal(
        () => leaveThenNavigate(link.href),
        () => (modalIsOpen = false),
      )
    }
  }

  async function leaveThenNavigate(href: string): Promise<void> {
    if (!cartStore.cart) {
      modalIsOpen = false
      locationAssign(href)
      return
    }
    for (const flowResolutionId of incompleteIds()) {
      const response = await cartStore.cart.deleteFlowResolution(flowResolutionId, true)
      if (response.status !== 'success') {
        modalIsOpen = false
        if (response.error) {
          errorTracker.track(response.error, {
            operation: 'incompleteBundleLeave.delete',
            flowResolutionId,
          })
        }
        globalError.set('An unexpected error occurred', { dismissable: true })
        return
      }
    }

    locationAssign(href)
  }

  onMounted(() => {
    document.addEventListener('click', onDocumentClick, true)
    window.addEventListener('beforeunload', onBeforeUnload)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('click', onDocumentClick, true)
    window.removeEventListener('beforeunload', onBeforeUnload)
  })
}
